import { z } from 'zod';
import { decimalStr, editorMediaMaxBytes, EDITOR_MEDIA_TYPES } from '../_shared/schemas.js';
import { PortalRoleSchema } from '../portal-access/schemas.js';
// A REAKCIÓ-ALAK a belső szál szerződéséből jön VÁLTOZATLANUL: egy fogalom, egy séma. Külön portál-változat
// azt jelentette volna, hogy a két felület csendben eltérő alakú chipeket rajzol ugyanarra az adatra.
import { TicketCommentReactionSchema, TicketStatusSchema, TicketTypeSchema, } from '../tickets/schemas.js';
/**
 * A PARTNER-PORTÁL olvasó szerződései. A `portal-access` csomag az ADMIN-oldali hozzáférés-KEZELÉSRŐL szól
 * (ki kap szerepet melyik fiókon); ez itt a PARTNER SAJÁT felülete: mit lát a bejelentkezett kapcsolattartó.
 *
 * KÖZÖS SZABÁLY MINDEN ITTENI VÁLASZRA: a mezők a LÁTHATÓSÁGI MAG (négy ág) szűrése UTÁN keletkeznek, és
 * **belső adat sosem kerül bele** — belső jegyzet, belső jog-kulcs, más fiók sora, belső kolléga e-mailje.
 * Amit a partner nem láthat, azt a szerver ki sem küldi (nem a UI rejti el).
 */
/** A jegy státusza a partner szemszögéből — ugyanaz az értékkészlet, mint a belső szerződésben (`@atlas/shared`). */
export const PortalTicketStatusSchema = TicketStatusSchema;
/**
 * A jegytípusok — a BELSŐ jegy-szerződéssel AZONOS értékkészlet (`tickets/schemas.ts`), mert ugyanabba az
 * `support/tickets` erőforrásba írunk. A felület a kulcsot i18n-ből fordítja.
 */
export const PortalTicketTypeSchema = TicketTypeSchema;
/**
 * A lista KÉT FÜLE (legacy paritás): a „folyamatban" a nyitott munkát mutatja, a „lezárt" az archívumot.
 * A szerver dönt, mely státusz melyik fülre esik — a UI nem sorol fel státuszokat, hogy egy új státusz ne
 * essen ki csendben egyik fülről sem.
 */
export const PortalTicketTabSchema = z.enum(['in_progress', 'closed']);
/** Egy sor a partner jegy-listáján. */
export const PortalTicketRowSchema = z.object({
    id: z.string(),
    /** Ember-olvasható jegy-szám (pl. `#TKT-2026/ABC123`); hiányozhat migrált sornál. */
    ticketNumber: z.string().nullable(),
    status: PortalTicketStatusSchema,
    /** Jegytípus-kulcs (a felület i18n-ből fordítja — nincs szerver-oldali magyar szöveg). */
    type: z.string(),
    accountId: z.string(),
    accountName: z.string(),
    subject: z.string(),
    /** A leírás rövid kivonata a listához (a teljes szöveg a részleten van). */
    descriptionExcerpt: z.string(),
    /** Az igénylő kapcsolattartó — név-részekkel, hogy a felület a NÉZŐ nyelvén rendezze a nevet. */
    requesterGivenName: z.string().nullable(),
    requesterFamilyName: z.string().nullable(),
    requesterName: z.string().nullable(),
    /** A hozzárendelt MORGENS-kolléga megjelenítendő neve (e-mail NEM megy ki a partnernek). */
    assigneeName: z.string().nullable(),
    /** A hozzárendelt kolléga presigned profilképe; `null` → monogram-fallback. */
    assigneeAvatarUrl: z.string().nullable(),
    /** a felelős kolléga csapat-színe (az avatar háttere) */
    assigneeTeamColor: z.string().nullable(),
    createdAt: z.string(),
    /** A LEZÁRÁS ideje (Odin-bélyeg) — nyitott, vagy ismeretlen idejű régi lezárt jegynél `null`. */
    closedAt: z.string().nullable(),
    /**
     * UTOLSÓ AKTIVITÁS: a jegy nyitása VAGY a legutóbbi KÜLSŐ válasz — amelyik később volt. A lista ezt mutatja
     * (a terv „Utolsó aktivitás" oszlopa), mert a partnert az érdekli, mikor történt valami, nem az, mikor
     * nyitotta. Belső jegyzet ebbe SEM számít bele (a partner nem is látja).
     */
    lastActivityAt: z.string(),
    /** A jegyen lévő KÜLSŐ válaszok száma (a terv „N válasz" jelzése az utolsó-aktivitás alatt). */
    replyCount: z.number().int().nonnegative(),
    /**
     * A PARTNER számára olvasatlan KÜLSŐ kommentek száma (belső jegyzet sosem számol bele), és a legutóbbi
     * olvasatlan időpontja. A legacy-paritás miatt a lista élére az olvasatlanok kerülnek.
     */
    unreadCommentCount: z.number().int().nonnegative(),
    latestUnreadCommentAt: z.string().nullable(),
});
/** A partner jegy-listájának szűrői — a fül és a szűrők az URL-ben élnek (linkelhető, F5-biztos nézet). */
export const PortalTicketListQuerySchema = z.object({
    tab: PortalTicketTabSchema.default('in_progress'),
    /**
     * EGY szabad-szöveges kereső — a jegyszámra, a TÁRGYRA, a LEÍRÁSRA, a FIÓK nevére, az IGÉNYLŐ és a
     * FELELŐS nevére illeszt (user-kérés: a felelős korábban kimaradt, holott a soron látszik —
     * egy megjelenített oszlopra, amire nem lehet keresni, a felhasználó a keresőt hibásnak olvassa).
     *
     * MIÉRT EGY, ÉS NEM ÖT OSZLOP-SZŰRŐ (a legacyben az volt): a partner-terv egyetlen keresőt ad
     * (a partner-terv szerint — a MEGÉPÜLT állapot: `docs/foundations/design.md` §9), a szűkítést pedig a FIÓK és a TÍPUS
     * választója viszi. Öt szabad-szöveges mező a partner-felületen zaj: a partnernek jellemzően néhány tucat
     * jegye van, nem ezer.
     */
    q: z.string().trim().max(200).optional(),
    /** Szűkítés EGY fiókra (a fiók-váltó és a szűrő-panel adja); hiányában a hívó MINDEN fiókja. */
    accountId: z.string().uuid().optional(),
    /** Szűkítés jegytípusra. */
    type: PortalTicketTypeSchema.optional(),
    /**
     * Szűkítés STÁTUSZRA — a FÜLÖN BELÜL (user-döntés). A fül a durva bontás (folyamatban /
     * lezárt), a státusz a finom: a „folyamatban" fülön a `nyitott` / `folyamatban` / `újranyitott` közül
     * lehet választani. A szerver a fül-szűrést a státusz-szűrés UTÁN is alkalmazza, tehát egy „lezárt"
     * státusz a „folyamatban" fülön üres listát ad — nem hazudik tele lapot.
     */
    status: PortalTicketStatusSchema.optional(),
    /**
     * LÉTREHOZÁS-DÁTUM tartomány (ISO date, INKLUZÍV mindkét végén) a `createdAt`-ra — a lista-toolbar
     * -tól/-ig mezői. UGYANAZ a szemantika, mint a belső jegy-listán (`TicketListQuerySchema`): a szűrés a
     * `createdAt` NAPJÁRA megy, tehát a `-ig` a megadott nap VÉGÉIG tart. Nyers ISO-összehasonlítással a
     * `2026-08-24` < `2026-08-24T10:00Z` reláció kizárná a megadott nap sorait.
     */
    createdFrom: z.string().optional(),
    createdTo: z.string().optional(),
    /**
     * LEZÁRÁS-DÁTUM tartomány a `closedAt` budapesti napjára (inkluzív) — a „Lezárt" fül szűrője, a belső listával
     * azonos szemantika; aktív szűrőnél a lezárási idő nélküli jegy kiesik.
     */
    closedFrom: z.string().optional(),
    closedTo: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(50),
    cursor: z.string().optional(),
    /**
     * A HÍVÓ nyelve — a NÉVSORREND miatt kell (magyarul vezetéknév elöl). A lista-oldalak mintája: a szerver
     * nem találgat Accept-Language-ből, a kliens küldi azt, amit épp mutat.
     */
    lang: z.enum(['hu', 'en']).optional(),
});
export const PortalTicketListResponseSchema = z.object({
    tickets: z.array(PortalTicketRowSchema),
    /** Összes találat a SZŰRÉS után (a lista lábában: „1–N / M jegy"). */
    total: z.number().int().nonnegative(),
    /**
     * A KÉT FÜL számlálói — a SZŰRŐK (kereső · fiók · típus) figyelembevételével, de a FÜL nélkül. Így a
     * fül-váltás előtt is látszik, mennyi van a másik oldalon (a terv fül-számlálói), és a szám ugyanazt a
     * szűrést tükrözi, amit a lista.
     */
    counts: z.object({
        in_progress: z.number().int().nonnegative(),
        closed: z.number().int().nonnegative(),
    }),
    nextCursor: z.string().nullable(),
});
/** Egy külső komment (válasz) a jegy-szálon. A BELSŐ jegyzet ide SOSEM kerül be. */
export const PortalTicketCommentSchema = z.object({
    id: z.string(),
    body: z.string(),
    /** A szerző megjelenítendő neve; `null`, ha nem oldható fel (nem küldünk e-mailt helyette). */
    authorName: z.string().nullable(),
    /**
     * A szerző FRISS presigned profilkép-URL-je; `null` → monogram-fallback (a felület egységes `Avatar`-ja).
     * A mezőnév a BELSŐ szál-szerződéssel egyezik (`authorAvatarUrl`) — egy fogalom, egy mezőnév az API-n.
     */
    authorAvatarUrl: z.string().nullable(),
    /** kolléga-szerzőnél a csapat-szín (avatar-háttér), partnernél null */
    authorTeamColor: z.string().nullable(),
    /** A szerző a partner SAJÁT oldaláról jött-e (a felület így tudja jobbra/balra igazítani). */
    authorIsPartner: z.boolean(),
    createdAt: z.string(),
    /** A szöveg utolsó szerkesztésének ideje (`null` = nem szerkesztett) — a „Szerkesztve" jelölés (Odin-bélyeg). */
    editedAt: z.string().nullable(),
    /** A PARTNER számára olvasatlan-e (a részlet megnyitása olvasottra állítja). */
    unread: z.boolean(),
    /**
     * EMOJI-REAKCIÓK ezen a kommenten (emojink összevonva, a reagálók listájával) — UGYANAZ az alak, mint a
     * belső szálon. A portálon a reakció a KÜLSŐ szálra van szűkítve: belső jegyzet nem is kerül ide, és az
     * Odin RLS-e kind-érzékeny (a partner a belső jegyzet reakcióit nem is látja).
     */
    reactions: z.array(TicketCommentReactionSchema),
    /**
     * Szerkeszthető-e a HÍVÓ számára: a komment SZERZŐJE ő, ÉS nem `portal-viewer`. A döntés SZERVER-oldali (a
     * `authorIsPartner` nem elég: az a partner-oldalt jelenti, nem a személyt — egy fiókon több kapcsolattartó
     * is van, és egymás válaszait nem szerkeszthetik).
     */
    canEdit: z.boolean(),
    /** OCC-verzió a szerkesztés `If-Match`-éhez (a busz-sor `version`-je). */
    version: z.number(),
});
/**
 * Egy melléklet a jegyen. A mező-készlet SZÁNDÉKOSAN szűk, és a `support/ticket-attachments` erőforráshoz van
 * mérve (mérve az Odin sémán): ott `filename` + `fileId` + `createdAt` van — **méret és MIME-típus NINCS**
 * (azok a presigned feltöltés paraméterei, nem tárolt oszlopok). Egy `sizeBytes`/`contentType` mező tehát
 * strukturálisan mindig `null` lenne: a felület kiírná a címkét, és soha nem lenne mögötte adat.
 *
 * A `filename` név a BELSŐ csatolmány-szerződéssel egyezik (`TicketAttachmentRowSchema`) — egy fogalom, egy
 * mezőnév az API-n.
 */
export const PortalTicketAttachmentSchema = z.object({
    id: z.string(),
    filename: z.string(),
    /**
     * FRISS presigned GET-URL (rövid TTL) — a bájtok KÖZVETLENÜL a tárból jönnek, sosem a BFF-en át (a belső
     * csatolmány-lista ugyanezt teszi). `null`, ha a sornak nincs `fileId`-ja (ETL-örökölt sor) vagy nem oldható
     * fel: a felület ilyenkor a nevet mutatja, letöltés-gomb nélkül. A presigned URL SOHA nem kerül logba.
     */
    downloadUrl: z.string().nullable(),
    createdAt: z.string(),
});
/**
 * Egy ELŐZMÉNY-esemény a jegyen (a terv „Előzmény" timeline-ja). A készlet SZŰK és a partner szemszögéből
 * értelmes: nyitás · válasz (külső) · státusz-váltás · lezárás. Belső jegyzet és belső mező-módosítás SOHA nem
 * kerül bele — az a munkatársak naplója.
 */
export const PortalTicketEventSchema = z.object({
    kind: z.enum(['opened', 'reply', 'status', 'closed']),
    at: z.string(),
    /** A szereplő megjelenítendő neve (e-mail SOHA); `null`, ha nem oldható fel vagy rendszer-esemény. */
    by: z.string().nullable(),
    /** Státusz-váltásnál az ÚJ státusz (a felirat i18n-ből jön). */
    status: PortalTicketStatusSchema.nullable(),
});
/** Egy kapcsolattartó a jegy-hozzáférés listáján / választójában. */
export const PortalTicketContactSchema = z.object({
    identityId: z.string(),
    name: z.string(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    /** FRISS presigned profilkép-URL; `null` → monogram-fallback. E-mail SOHA nem megy ki helyette. */
    avatarUrl: z.string().nullable(),
    /** A fiókon elsődleges kapcsolattartó-e (ő amúgy is mindent lát — a listán jelöljük). */
    isPrimary: z.boolean(),
    /** Van-e EXPLICIT jegy-hozzáférése ehhez a jegyhez. */
    hasAccess: z.boolean(),
    /**
     * A HÍVÓ maga-e ez a kapcsolattartó. A felület ebből jelöli („Ön") a saját sorát, és a hozzáférés-osztó
     * listából is ezt zárja ki — a saját hozzáférését senki nem vonhatja vissza (a szerver is így számol).
     */
    isSelf: z.boolean(),
});
export const PortalTicketDetailSchema = z.object({
    id: z.string(),
    ticketNumber: z.string().nullable(),
    status: PortalTicketStatusSchema,
    type: z.string(),
    accountId: z.string(),
    accountName: z.string(),
    subject: z.string(),
    description: z.string(),
    requesterGivenName: z.string().nullable(),
    requesterFamilyName: z.string().nullable(),
    requesterName: z.string().nullable(),
    /** Az igénylő presigned profilképe; `null` → monogram-fallback (a felület `PersonChip`-je). */
    requesterAvatarUrl: z.string().nullable(),
    assigneeName: z.string().nullable(),
    /** A felelős (MORGENS-kolléga) presigned profilképe; `null` → monogram-fallback. */
    assigneeAvatarUrl: z.string().nullable(),
    /** a felelős kolléga csapat-színe (az avatar háttere) */
    assigneeTeamColor: z.string().nullable(),
    createdAt: z.string(),
    /** Fájlmegosztó linkek (opcionálisak) — a legacy három mezője. */
    googleDriveLink: z.string().nullable(),
    microsoftOnedriveLink: z.string().nullable(),
    dropboxLink: z.string().nullable(),
    comments: z.array(PortalTicketCommentSchema),
    attachments: z.array(PortalTicketAttachmentSchema),
    /** ELŐZMÉNY (timeline) — időrendben, a partner számára értelmes eseményekből. */
    events: z.array(PortalTicketEventSchema),
    /**
     * A JEGY-HOZZÁFÉRÉS listája: a fiók kapcsolattartói, ki látja ezt a jegyet. CSAK `canManageAccess` esetén
     * jön ki (a jegy nyitója vagy a fiók elsődlegese) — különben ÜRES tömb: más kapcsolattartók névsora nem a
     * partner dolga.
     */
    contacts: z.array(PortalTicketContactSchema),
    /**
     * A HÍVÓ jogköre EZEN a jegyen — a felület ebből dönt, mit ajánl fel (és a szerver ugyanezt érvényesíti):
     *   • `canReply` — írhat választ (a fiókon `portal.tickets.write` joga van);
     *   • `canManageAccess` — oszthat jegy-hozzáférést: TULAJDONLÁS-alapú (a jegy nyitója vagy a fiók
     *     elsődleges kapcsolattartója), és jegy-írás jog is kell, mert a megosztás írás.
     * A jegy státuszát a partner SOHA nem állítja (user-döntés): nincs újranyitás/lezárás innen.
     */
    canReply: z.boolean(),
    canManageAccess: z.boolean(),
    /** A hívó fiókonkénti szerepe — a felület jelzéséhez (nem jog-döntéshez). */
    role: PortalRoleSchema,
});
/** Egy szolgáltatás-opció a jegy-nyitó lánc végén. */
export const PortalServiceOptionSchema = z.object({
    id: z.string(),
    name: z.string(),
});
/**
 * A jegy-nyitó űrlap opciói. A LÁNC: fiók → szolgáltatás → típus. Ügyfél-választó SZÁNDÉKOSAN nincs: a partner
 * a FIÓKJAIT látja (a fiók neve mellé az ügyfél nevét írjuk ki), így egy lépéssel rövidebb a folyamat, és nem
 * lehet olyan ügyfelet választani, amihez nincs portál-hozzáférésű fiók.
 */
export const PortalTicketFormOptionsResponseSchema = z.object({
    /** A hívó portál-hozzáférésű fiókjai — CSAK ezekre nyithat jegyet. */
    accounts: z.array(z.object({
        id: z.string(),
        name: z.string(),
        clientName: z.string().nullable(),
        /** A hívó szerepe ezen a fiókon — jelzéshez, nem jog-döntéshez. */
        role: PortalRoleSchema,
        /** Nyithat-e jegyet ezen a fiókon (`portal.tickets.write`) — a felület tiltja, a szerver is érvényesíti. */
        canOpen: z.boolean(),
    })),
    /**
     * A választott fiók szolgáltatásai KÉT csoportban (legacy paritás): `existing` = amit a fiók használ,
     * `other` = a katalógus többi eleme. Csak akkor jön, ha az `accountId` query meg volt adva.
     */
    services: z
        .object({
        existing: z.array(PortalServiceOptionSchema),
        other: z.array(PortalServiceOptionSchema),
    })
        .nullable(),
    types: z.array(PortalTicketTypeSchema),
    /**
     * `true` → a jegy-NYITÁS ki sem szolgálható ebben a környezetben: a szolgáltatás-katalógus (`shared`
     * adat-osztály) csak GÉPI Odin-kredenciállal olvasható, és az nincs beállítva. A felület ilyenkor
     * MAGYARÁZATOT mutat, nem „Újra" gombos hiba-állapotot: az újratöltés ettől soha nem lesz jobb (a `serviceId`
     * kötelező, tehát üres listával jegyet sem lehet nyitni). Az ÁTMENETI upstream-hiba továbbra is hiba-állapot.
     * Ugyanaz a minta, mint az üzenetek `unavailable` jelzője.
     */
    unavailable: z.boolean(),
});
/** A jegy-nyitás body-ja. A szerver a KÉRELMEZŐT a tokenből veszi — a partner nem nyithat más nevében. */
export const PortalTicketCreateBodySchema = z.object({
    accountId: z.string().uuid(),
    serviceId: z.string().uuid(),
    type: PortalTicketTypeSchema.default('support'),
    subject: z.string().trim().min(1).max(255),
    /**
     * A leírás — a szerver a LÁTHATÓ (plain-text) hosszán érvényesíti a **min. 25 karaktert** (legacy szabály),
     * tehát a markup nem csal be. A kliens ugyanígy validál, hogy a hiba ne a mentésnél derüljön ki.
     */
    description: z.string().min(1),
    googleDriveLink: z.string().url().max(500).nullable().optional(),
    microsoftOnedriveLink: z.string().url().max(500).nullable().optional(),
    dropboxLink: z.string().url().max(500).nullable().optional(),
});
/**
 * A SZERKESZTETT válasz — a szerkesztés SZŰK válasza. Csak az van benne, ami a művelettől megváltozott: a
 * szöveg és az OCC-verzió (a következő szerkesztés `If-Match`-e). A `createdAt` a NYITÁS ideje (a szerkesztés
 * nem írja át) — a felület ezzel tartja a sor helyét a szálon.
 */
export const PortalTicketCommentEditedSchema = z.object({
    id: z.string(),
    body: z.string(),
    version: z.number(),
    createdAt: z.string(),
    /** A szerkesztés ideje (Odin-bélyeg) — a „Szerkesztve" jelölés. `null`, ha a szöveg nem változott. */
    editedAt: z.string().nullable(),
});
/** Válasz (külső komment) a jegy-szálra. A `kind`-ot a SZERVER dönti el: partnertől mindig `external`. */
export const PortalTicketReplyBodySchema = z.object({
    body: z.string().trim().min(1).max(20000),
});
/**
 * A PARTNER-SHELL adatai EGY kérésben (`GET /portal/me`) — a felület kerete ebből épül fel.
 *
 * MIÉRT EGY VÉGPONT: a sidebar fiók-váltója, a felhasználó-kártya és a nav-jelvények UGYANABBÓL a három
 * tényből élnek (ki vagyok · mely fiókjaim vannak · mennyi vár rám). Három külön hívás helyett egy — a shell
 * minden lapon ott van, tehát ez a leggyakoribb kérés a portálon.
 */
export const PortalMeResponseSchema = z.object({
    identity: z.object({
        /** A megjelenítendő név (a névsorrend a hívó nyelvén — a `lang` query dönti). */
        name: z.string(),
        givenName: z.string().nullable(),
        familyName: z.string().nullable(),
        /** A SAJÁT becenév — a köszöntés elsődleges forrása (`addressName`: becenév → keresztnév); nincs → null. */
        nickname: z.string().nullable(),
        email: z.string().nullable(),
        avatarUrl: z.string().nullable(),
    }),
    /** A hívó portál-fiókjai — a fiók-váltó és a jegy-nyitó fiók-listája ebből épül. */
    accounts: z.array(z.object({
        id: z.string(),
        name: z.string(),
        clientId: z.string().nullable(),
        clientName: z.string().nullable(),
        /** A fiókra szóló portál-szerep (`portal-admin|member|viewer`). */
        role: PortalRoleSchema,
        /**
         * A szerep feature-jogai EZEN a fiókon (`@atlas/shared` `PORTAL_PERMISSIONS`) — a menü és a gombok ebből
         * kapuznak, nem a szerep nevéből (a szerep tartalma a jogosultság-felületen szerkeszthető). A fal a BFF-ben.
         */
        permissions: z.array(z.string()),
        /** A hívó a fiók ELSŐDLEGES kapcsolattartója-e (ez dönti el, mennyit LÁT). */
        isPrimary: z.boolean(),
    })),
    /**
     * A nav-jelvények számai. A `null` azt jelenti: **nem tudjuk** — a modul ki sem szolgálható (nincs gépi
     * Odin-kredenciál), vagy az upstream hibázott. A felület ilyenkor NEM mutat jelvényt (a 0 azt állítaná,
     * hogy „megnéztük, nincs semmi").
     */
    counts: z.object({
        /** Folyamatban lévő (nyitott · dolgozunk rajta · újranyitott) jegyek száma. */
        openTickets: z.number().int().nonnegative(),
        /** Olvasatlan válaszos JEGYEK száma (nem a kommentek száma). */
        unreadTickets: z.number().int().nonnegative(),
        unreadMessages: z.number().int().nonnegative().nullable(),
        overdueInvoices: z.number().int().nonnegative().nullable(),
    }),
});
/** A jegy-hozzáférés beállítása: a KIVÁLASZTOTT kapcsolattartók listája (teljes állapot, nem delta). */
export const PortalTicketAccessBodySchema = z.object({
    /**
     * Azok az identity-k, akik LÁTHATJÁK a jegyet. Teljes állapot: a listából kimaradt hozzáférés VISSZAVONÓDIK
     * (a felület jelölő-listát mutat, tehát a delta-számolás a szerveren pontosabb és ütközés-mentes).
     * Az ELSŐDLEGES kapcsolattartó és a jegy nyitója nem szerepelhet benne értelmesen — ők amúgy is látják.
     */
    identityIds: z.array(z.string().uuid()).max(50),
});
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
// SZÁMLÁIM (a terv §7.6) — a partner a SAJÁT fiókjaihoz tartozó számláit látja.
//
// PÉNZ: minden összeg DECIMAL-STRING (sosem `number`) — a repo adat-szabálya. A devizát MINDIG mellé adjuk:
// összeg deviza nélkül értelmezhetetlen, és a partnernek EUR-os tétele is lehet.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
/** A számla-lista HÁROM füle (legacy paritás): kiállított (esedékes+lejárt) · kiegyenlített · érvénytelenített. */
export const PortalInvoiceTabSchema = z.enum(['issued', 'paid', 'storno']);
/**
 * KÖZÖS SZÁMLÁZÁS jelölése: a tétel / számla nem csak a szűrt fiókra, hanem több fiókra EGYÜTT vonatkozik (az
 * ügyfél egy számlát kap a fiókjaira). A fióknevek közül csak azok jönnek, amelyekhez a hívónak hozzáférése van —
 * a többi csak darabszámként (`otherCount`), más fiók nevét a partner nem láthatja.
 */
export const PortalSharedSchema = z.object({
    accountCount: z.number().int().nonnegative(),
    visibleAccountNames: z.array(z.string()),
    otherCount: z.number().int().nonnegative(),
});
export const PortalInvoiceRowSchema = z.object({
    id: z.string(),
    invoiceNumber: z.string(),
    /** `issued` | `paid` | `overdue` | `storno` | `cancelled` — a felület i18n-ből fordítja. */
    status: z.string(),
    /** `invoice` | `proforma` — a díjbekérő máshogy olvasandó (még nem számla). */
    type: z.string(),
    accountId: z.string().nullable(),
    accountName: z.string().nullable(),
    /** A VEVŐ (számlázási) neve és címe — a partner a saját számláján ezt ellenőrzi. */
    billingName: z.string().nullable(),
    billingAddress: z.string().nullable(),
    currency: z.string(),
    net: decimalStr,
    vat: decimalStr,
    gross: decimalStr,
    /** Nap-pontos dátumok (`YYYY-MM-DD`) — zóna-konverzió NÉLKÜL. */
    issueDate: z.string().nullable(),
    dueDate: z.string().nullable(),
    paymentDate: z.string().nullable(),
    /**
     * Letölthető-e a PDF. A státusz dönti el (`issued|paid|overdue|storno`) — piszkozatnál nincs mit letölteni.
     * A felület ebből tiltja a gombot, a szerver pedig ugyanezt érvényesíti (409).
     */
    canDownloadPdf: z.boolean(),
    /** Közös számla több fiókra (az ügyfél-számla `invoice-accounts` pivotjából) — `null`: egy fiókra / nincs pivot. */
    shared: PortalSharedSchema.nullable(),
});
/** Egy számla-TÉTEL, ahogy a számlán áll. */
export const PortalInvoiceItemSchema = z.object({
    name: z.string(),
    /** A tétel számlára kerülő megjegyzése. */
    note: z.string().nullable(),
    /** Az elszámolt időszak (`YYYY-MM-DD`). */
    periodStart: z.string().nullable(),
    periodEnd: z.string().nullable(),
    quantity: decimalStr,
    quantityUnit: z.string().nullable(),
    unitPrice: decimalStr,
    vatRate: z.string().nullable(),
    net: decimalStr,
    vat: decimalStr,
    gross: decimalStr,
});
/** Egy számla tételesen — a havi áttekintés TÉNY-oldala és a Számláim lista kibontása. */
export const PortalInvoiceDetailSchema = PortalInvoiceRowSchema.extend({
    /** A számlára kerülő megjegyzés (a belső megjegyzés SOHA nem jön ki). */
    note: z.string().nullable(),
    items: z.array(PortalInvoiceItemSchema),
});
/**
 * Egy TERV-tétel: egy szolgáltatás, ami az adott hónapban esedékes, de még nincs rá számla. Az összeg a belső
 * bevétel-motor szabályával számol (gyakoriság, előre / utólag fizetés, első hónap arányosítása, záró dátum) — a
 * SZÁMLÁZÁS hónapjára (amikor a partner fizet): a negyedéves / éves díj egyben, a számla hónapjában.
 */
export const PortalPlanItemSchema = z.object({
    assignmentId: z.string(),
    serviceName: z.string(),
    /** `monthly` | `quarterly` | `semi_annual` | `annual` | `one_time` */
    frequency: z.string().nullable(),
    /** Az elszámolt időszak (`YYYY-MM-DD`). */
    periodStart: z.string().nullable(),
    periodEnd: z.string().nullable(),
    /** Nettó összeg; `null`, ha nem becsülhető (használat-alapú tétel, korábbi tény nélkül). */
    amount: decimalStr.nullable(),
    currency: z.string().nullable(),
    /** Használat-alapú tétel: az összeg BECSLÉS (az utolsó ismert számla-tétel alapján). */
    estimated: z.boolean(),
    /** Az első hónap arányosított díja. */
    prorated: z.boolean(),
    shared: PortalSharedSchema.nullable(),
});
/** Egy hónap a partner számlázási idővonalán. */
/**
 * KORLÁTOZOTT CSOPORTOS SZÁMLÁZÁS (user-döntés): a csoportos (több fiókra szóló) számlát / díj-tételt az látja,
 * akinek az ÖSSZES érintett fiókjára „Szolgáltatások és díjak" joga van. Akinek csak néhányra, az a tartalmát nem
 * látja — csak azt, hogy LÉTEZIK, és hány ilyen tétel szól (az ő fiókjára is). Tartalom (összeg, tétel, más fiók
 * neve) itt soha nincs, csak darabszám.
 */
export const PortalHiddenSharedSchema = z.object({
    /** A hónapban kelt / a szűrésnek megfelelő, de nem megtekinthető csoportos számlák száma. */
    invoices: z.number().int().nonnegative(),
    /** A hónapban esedékes, nem megtekinthető csoportos díj-tételek száma. */
    planItems: z.number().int().nonnegative(),
});
export const PortalBillingMonthSchema = z.object({
    /** `YYYY-MM` */
    month: z.string(),
    /** `actual` — csak számla · `plan` — még semmi nincs számlázva · `mixed` — számla + még nem számlázott tétel */
    basis: z.enum(['actual', 'plan', 'mixed']),
    /** A hónapban kelt számlák (a piszkozat is — `status: draft`, PDF nélkül), a stornó / érvénytelenített NEM. */
    invoices: z.array(PortalInvoiceDetailSchema),
    /** A hónapban esedékes, de még nem számlázott tételek. */
    planItems: z.array(PortalPlanItemSchema),
    /** A hónap nettó összege devizánként (tény + terv); `estimated`: van benne becsült tétel. */
    totals: z.array(z.object({ currency: z.string(), net: decimalStr, estimated: z.boolean() })),
    /** A nem megtekinthető csoportos tételek száma a hónapban (az összeg NEM tartalmazza őket). */
    hiddenShared: PortalHiddenSharedSchema,
});
export const PortalBillingQuerySchema = z.object({
    accountId: z.string().uuid().optional(),
    /** `YYYY-MM` — alap: az idei év január */
    from: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
        .optional(),
    /** `YYYY-MM` — alap: a mostani hónap + 3 */
    to: z
        .string()
        .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
        .optional(),
    lang: z.enum(['hu', 'en']).optional(),
});
export const PortalBillingResponseSchema = z.object({
    /** A mostani hónap (`YYYY-MM`, Europe/Budapest) — a felület ezt nyitja meg. */
    current: z.string(),
    months: z.array(PortalBillingMonthSchema),
});
export const PortalInvoiceListQuerySchema = z.object({
    tab: PortalInvoiceTabSchema.default('issued'),
    /** Szabad-szöveg a számlaszámra és a vevő nevére. */
    q: z.string().trim().max(200).optional(),
    accountId: z.string().uuid().optional(),
    lang: z.enum(['hu', 'en']).optional(),
});
export const PortalInvoiceListResponseSchema = z.object({
    invoices: z.array(PortalInvoiceRowSchema),
    /** A három fül számlálói a SZŰRT halmazon (a fül nélkül) — a váltás előtt is látszik, mennyi van odaát. */
    counts: z.object({
        issued: z.number().int().nonnegative(),
        paid: z.number().int().nonnegative(),
        storno: z.number().int().nonnegative(),
    }),
    /**
     * LEJÁRT számlák összesítése a lap tetejére. A `total` DEVIZÁNKÉNT bontott: több deviza esetén nincs
     * értelmes egyetlen szám (átszámítani csak tájékoztatóan lehetne — azt nem tesszük egy fizetési felszólítás
     * helyére).
     */
    overdue: z.object({
        count: z.number().int().nonnegative(),
        totals: z.array(z.object({ currency: z.string(), gross: decimalStr })),
    }),
    /** A szűrésnek megfelelő, de nem megtekinthető csoportos számlák száma (lásd `PortalHiddenSharedSchema`). */
    hiddenSharedCount: z.number().int().nonnegative(),
});
/**
 * Egy SZOLGÁLTATÁS a partner szemszögéből, a hozzárendelés-sorokból összevonva: ugyanaz a szolgáltatás (csomag)
 * időszakonként más áron több hozzárendelés-sorban élhet — a partnernek egy szolgáltatás, időszakokkal.
 */
export const PortalServicePeriodSchema = z.object({
    /** `YYYY-MM` */
    from: z.string().nullable(),
    /** `YYYY-MM`; `null` → határozatlan */
    to: z.string().nullable(),
    /** Nettó díj a gyakoriság egy időszakára; `null`, ha nem becsülhető. */
    amount: decimalStr.nullable(),
    currency: z.string().nullable(),
    estimated: z.boolean(),
    /** `active` | `pending` */
    status: z.string(),
});
export const PortalServiceGroupSchema = z.object({
    serviceName: z.string(),
    /** `monthly` | `quarterly` | `semi_annual` | `annual` | `one_time` */
    frequency: z.string().nullable(),
    /** Használat-alapú díjazás — az összeg hónapról hónapra változhat. */
    usageBased: z.boolean(),
    /**
     * Az EGY fiókra szóló szolgáltatás fiókjának neve (ha a hívó láthatja) — így ugyanaz a szolgáltatás két külön
     * fiókon nem két megkülönböztethetetlen kártya. Több fióknál `null`, ott a `shared` jelöl.
     */
    accountName: z.string().nullable(),
    shared: PortalSharedSchema.nullable(),
    /**
     * KORLÁTOZOTT: csoportosan számlázott szolgáltatás, ami olyan fiókot is érint, amihez a hívónak nincs díj-joga
     * → a szolgáltatás és az időszakai látszanak, a díj NEM (`amount: null`), és a következő havi díjba sem számít.
     */
    restricted: z.boolean(),
    /** Az időszakok időrendben (a múltbeli is, a mai év elejétől). */
    periods: z.array(PortalServicePeriodSchema),
});
export const PortalServicesResponseSchema = z.object({
    services: z.array(PortalServiceGroupSchema),
    /**
     * A KÖVETKEZŐ HAVI VÁRHATÓ DÍJ. Devizánként bontva (lásd az `overdue.totals` érvelését), és a
     * `hasVariable` jelzi, ha használat-alapú tétel is esedékes — az összeg akkor csak tájékoztató.
     */
    nextMonth: z.object({
        /** A hónap első napja (`YYYY-MM-01`) — a felület ebből írja ki a hónap nevét a néző nyelvén. */
        month: z.string(),
        totals: z.array(z.object({ currency: z.string(), amount: decimalStr })),
        items: z.array(z.object({ serviceName: z.string(), amount: decimalStr, currency: z.string() })),
        hasVariable: z.boolean(),
    }),
});
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
// ÜZENETEK ÉS MEGHÍVÓK (a terv §7.5)
//
// MIÉRT OLVASSA A BFF GÉPI IDENTITÁSSAL: a `communication.messages` / `meetings` az Odinban NEM
// tenant-scope-olt (nincs `client_id`) — az RLS-e `app.see_all`-t kér, amit partner-token SOSEM kap. A
// jogosultság-döntés ezért TELJESEN a BFF-en van, és szűk: csak KIKÜLDÖTT (`sent`), KÜLSŐ (`external`)
// üzenet, és csak akkor, ha a hívó a címzettje — személyesen, a fiókján át, vagy ügyfél-szintű közleményként.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────
/** Az üzenet CÍMZÉSE a partner szemszögéből — ebből lesz a felület jelölése („közlemény" / „személyes"). */
export const PortalMessageScopeSchema = z.enum(['broadcast', 'account', 'contact']);
export const PortalMessageRowSchema = z.object({
    id: z.string(),
    subject: z.string(),
    /** A törzs első pár sora sima szövegre butítva (a teljes törzs a részleten). */
    preview: z.string(),
    /** A feladó megjelenítendő neve (belső kolléga) — e-mail SOHA nem megy ki. */
    from: z.string().nullable(),
    sentAt: z.string(),
    scope: PortalMessageScopeSchema,
    attachmentCount: z.number().int().nonnegative(),
    unread: z.boolean(),
    /** A hívó ARCHIVÁLTA (olvasó-oldali — a saját olvasás-során; a többi címzettnél az üzenet megmarad). */
    archivedByMe: z.boolean(),
});
/** Egy megbeszélés-meghívó + a hívó VÁLASZA (RSVP). */
export const PortalMeetingRowSchema = z.object({
    id: z.string(),
    title: z.string(),
    startsAt: z.string(),
    /** Hossz percben (a `startsAt`/`endsAt` párból); `null`, ha nincs vége megadva. */
    durationMinutes: z.number().int().positive().nullable(),
    /** Helyszín: a fizikai helyszín kulcsa VAGY az online link (a felület ikont választ belőle). */
    locationKey: z.string().nullable(),
    onlineLink: z.string().nullable(),
    organizerName: z.string().nullable(),
    /** A szervező presigned profilképe; `null` → monogram-fallback. */
    organizerAvatarUrl: z.string().nullable(),
    /** a szervező kolléga csapat-színe (avatar-háttér) */
    organizerTeamColor: z.string().nullable(),
    /** A HÍVÓ visszajelzése: `pending` | `confirmed` | `declined`. */
    rsvp: z.enum(['pending', 'confirmed', 'declined']),
});
export const PortalMessagesResponseSchema = z.object({
    messages: z.array(PortalMessageRowSchema),
    meetings: z.array(PortalMeetingRowSchema),
    unreadCount: z.number().int().nonnegative(),
    /**
     * `true` → a modul KI SEM SZOLGÁLHATÓ ebben a környezetben (nincs gépi Odin-kredenciál). A felület ilyenkor
     * magyarázatot mutat, NEM üres listát: az „nincs üzenete" hazugság lenne.
     */
    unavailable: z.boolean(),
});
/** Egy üzenet TELJES tartalma (a lap beúszó paneljéhez). */
export const PortalMessageDetailSchema = z.object({
    id: z.string(),
    subject: z.string(),
    /** A törzs SZANITÁLT HTML-ként (a megjelenítés a közös rich-text komponensen megy). */
    body: z.string(),
    from: z.string().nullable(),
    /** A feladó presigned profilképe; `null` → monogram-fallback. */
    fromAvatarUrl: z.string().nullable(),
    /** a feladó kolléga csapat-színe (avatar-háttér) */
    fromTeamColor: z.string().nullable(),
    sentAt: z.string(),
    scope: PortalMessageScopeSchema,
    attachments: z.array(z.object({
        id: z.string(),
        filename: z.string(),
        /** FRISS presigned GET-URL; `null`, ha nincs mögötte fájl (ETL-örökölt sor). */
        downloadUrl: z.string().nullable(),
    })),
});
/** RSVP-válasz. A `pending`-re visszaállás is engedett (a terv „Válasz módosítása" gombja). */
export const PortalMeetingRsvpBodySchema = z.object({
    status: z.enum(['pending', 'confirmed', 'declined']),
});
/**
 * A PARTNER-oldali WYSIWYG-kép feltöltés-kérése. A belső (`EditorImageUploadBodySchema`) MÁSA, EGY
 * SZIGORÍTÁSSAL: az `accountId` **KÖTELEZŐ**.
 *
 * MIÉRT: a belső oldalon a fiók elhagyható, és a fájl gazdája ilyenkor a BELSŐ (MORGENS) ügyfél-rekord — „a
 * tartalmat a cég írja, tehát a kép is a cégé". A partner-oldalon ez a fallback nem létezhet: a partnernek
 * SEMMILYEN írása nem kerülhet a MORGENS tenantjába, és nincs is olyan felülete, ahol ne lenne cél-fiók (a
 * jegy-nyitó 1. lépése épp a fiókot választja ki). A cél-ÜGYFELET a BFF a hívó SAJÁT portál-hozzáféréséből
 * oldja fel — idegen fiók id-je 403, akkor is, ha létezik.
 */
export const PortalEditorImageUploadBodySchema = z
    .object({
    accountId: z.string().uuid(),
    filename: z.string().trim().min(1).max(255),
    /** KÉP vagy rövid VIDEÓ — a KÖZÖS `EDITOR_MEDIA_TYPES` whitelist (a belső oldallal azonos). */
    contentType: z.enum(EDITOR_MEDIA_TYPES),
    /** Bájt-méret; a plafon típus-függő (kép 5 MB, videó 25 MB) — lásd a `superRefine`-t. */
    sizeBytes: z.number().int().positive(),
})
    .superRefine((v, ctx) => {
    const max = editorMediaMaxBytes(v.contentType);
    if (v.sizeBytes > max) {
        ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            maximum: max,
            type: 'number',
            inclusive: true,
            path: ['sizeBytes'],
            message: `A fájl mérete meghaladja a ${String(Math.round(max / (1024 * 1024)))} MB-os korlátot.`,
        });
    }
});
/** A partner-oldali commit kérése — a cél-fiók itt is kötelező (a tenant abból oldódik fel). */
export const PortalEditorImageCommitBodySchema = z.object({ accountId: z.string().uuid() });
/**
 * A partner-oldali commit VÁLASZA — a belsőhöz képest EGY mezővel több.
 *
 * `url`: a HTML-be MENTENDŐ, STABIL út (`/editor-images/{fileId}/content`) — ugyanaz az alak, mint a belső
 * oldalon, mert UGYANEZT a tárolt HTML-t a belső felület is rendereli.
 *
 * `displayUrl`: FRISS presigned GET-URL, KIZÁRÓLAG az AZONNALI, szerkesztőn belüli előnézethez. MIÉRT KELL:
 * a szerkesztőbe beszúrt kép a stabil utat viseli, azt pedig a böngésző nem tudja betölteni (a BFF
 * Bearer-tokent kér, egy `<img>` nem küld fejlécet), a feloldó végpont viszont a JEGYHEZ kötött — a
 * még nem létező jegy képére nincs mit kötni. A `displayUrl` a mentett HTML-be SOHA nem kerül (lejárna).
 */
export const PortalEditorImageCommitSchema = z.object({
    url: z.string(),
    displayUrl: z.string(),
});
/** A jegyhez tartozó beágyazott kép megjelenítési URL-je (friss presigned GET). */
export const PortalTicketImageSchema = z.object({ url: z.string() });
//# sourceMappingURL=schemas.js.map